<template>
    <div v-if="question.isLabel" class="mb-10px">
        <h3 class="form-label" :id="question.id + '_fq'">{{ question.caption }}</h3>
    </div>

    <el-divider v-else-if="question.isPageBreak"></el-divider>

    <el-form
        v-else
        ref="form"
        :model="question"
        label-position="top"
        class="ml-5px"
        @submit.native.prevent
    >
        <el-form-item
            class="position-relative"
            :id="question.id + '_fq'"
            prop="data.return_value"
            :rules="
                question.mandatory
                    ? [{ required: true, message: $t('Field is required'), trigger: 'blur' }]
                    : []
            "
        >
            <template slot="label" v-if="showCaption">
                <span class="form-label">{{ question.caption }}</span>
            </template>

            <div
                v-if="question.help_text || (question.help_docs && question.help_docs.length > 0)"
                class="help-documents-button"
            >
                <span class="fa fa-question text-primary" @click="onHelpDocumentsClick"></span>
            </div>

            <div
                v-if="question.data.comments && question.data.comments.length > 0"
                class="document-comment-button"
            >
                <span
                    class="fa fa-commenting"
                    :class="{
                        'text-danger': question.data.comments && question.data.comments.length > 0,
                    }"
                    @click="onCommentButtonTap"
                ></span>
            </div>
            <div v-if="showAddRi" class="document-ri-button">
                <span class="fa fa-plus" @click="onRITap"> {{ $t('Add RI') }}</span>
            </div>
            <div
                v-if="
                    question.isMedia === false &&
                    (question.data.return_value ||
                        question.data.notes ||
                        question.data.docs.length > 0)
                "
                class="document-upload-button"
            >
                <span
                    class="fa fa-plus"
                    :class="{
                        'text-primary': question.data.notes || question.data.docs.length > 0,
                    }"
                    @click="onDocumentUploadTap"
                ></span>
            </div>

            <textfield
                v-if="question.isTextBox || question.isTextArea || question.isNCBValidatorType"
                :question="question"
            ></textfield>

            <numberfield
                v-if="question.isNumber"
                v-model="question.data.return_value"
                :disabled="!!question.calc_method"
            ></numberfield>

            <money-input
                v-if="question.isMoney"
                @onChange="question.data.return_value = $event"
                :value="question.data.return_value"
            ></money-input>

            <template v-else-if="question.isCoordinate">
                <addresscoordinatesfield v-model="coordinates" />
            </template>

            <el-date-picker
                v-else-if="question.isDate"
                v-model="question.data.return_value"
                :format="$store.getters.dateDisplayFormat"
                :picker-options="datePickerOptions"
                type="date"
                :placeholder="$t('Pick a day')"
                value-format="yyyy-MM-dd"
                size="small"
            ></el-date-picker>

            <el-date-picker
                v-else-if="question.isDateMonth"
                v-model="question.data.return_value"
                :format="$store.getters.dateDisplayMonthFormat"
                :picker-options="datePickerOptions"
                type="month"
                :placeholder="$t('Pick a month/year')"
                value-format="yyyy-MM-dd"
                size="small"
            ></el-date-picker>

            <timepicker
                v-else-if="question.isTime"
                size="small"
                v-model="question.data.return_value"
                :picker-options="{
                    start: '07:00',
                    step: '00:15',
                    end: '19:00',
                }"
                :placeholder="$t('Select time')"
            >
            </timepicker>

            <selection
                v-else-if="question.isListType || question.isSICComponent"
                :question="question"
                :section="section"
                class="w-100"
                :ruleparser="ruleparser"
            />

            <el-switch
                v-model="question.data.return_value"
                v-else-if="question.isYesNo"
                active-value="Yes"
                inactive-value="No"
            ></el-switch>
            <div v-if="question.isLabelImage">
                <el-image
                    :src="getImageSrc({ file_storage_id: question.default_value }, 600, 600)"
                    :preview-src-list="[
                        getImageSrc({ file_storage_id: question.default_value }, null, null),
                    ]"
                >
                    <div slot="placeholder" class="image-slot label-image-placeholder">
                        Loading<span class="dot">...</span>
                    </div>
                </el-image>
            </div>
            <template v-else-if="question.isImage">
                <el-upload
                    ref="upload"
                    class="file-upload-image"
                    list-type="picture-card"
                    action="/mobile/file/upload"
                    multiple
                    accept="image/*"
                    :file-list="questionFiles"
                    :auto-upload="false"
                    :on-change="onImageUpload"
                >
                    <i slot="trigger" class="el-icon-plus"></i>
                    <el-button
                        class="el-upload el-upload--picture-card"
                        style="margin-left: 10px"
                        icon="el-icon-upload2"
                        @click="onOpenVisitLibraryDialog('Image')"
                    >
                        {{ $t('Visit Library') }}
                    </el-button>
                    <div slot="file" slot-scope="{ file }">
                        <img
                            v-if="!file.uploading"
                            class="el-upload-list__item-thumbnail"
                            :src="getImageSrc(file)"
                            alt
                        />
                        <div v-else class="position-relative text-align-center">
                            <div class="position-absolute w-100 h-100">
                                <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                            </div>
                        </div>
                        <div
                            v-if="!file.uploading"
                            class="position-absolute"
                            style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                        >
                            <span @click="handleImageTag(file.id)">
                                <i class="fa fa-tag"></i>
                            </span>
                            <span @click="handleRemove(file)">
                                <i class="fa fa-trash text-danger"></i>
                            </span>
                            <span @click="handlePictureCardPreview(file)">
                                <i class="el-icon-zoom-in"></i>
                            </span>
                        </div>
                    </div>
                </el-upload>
            </template>

            <template v-if="question.isVideo">
                <el-upload
                    ref="upload"
                    class="file-upload-video"
                    list-type="picture-card"
                    action="/mobile/file/upload"
                    multiple
                    accept="video/*"
                    :file-list="questionFiles"
                    :auto-upload="false"
                    :on-change="onFileUpload"
                >
                    <i slot="trigger" class="el-icon-plus"></i>
                    <el-button
                        class="el-upload el-upload--picture-card"
                        style="margin-left: 10px"
                        icon="el-icon-upload2"
                        @click="onOpenVisitLibraryDialog('Video')"
                    >
                        {{ $t('Visit Library') }}
                    </el-button>

                    <div slot="file" slot-scope="{ file }">
                        <video v-if="!file.uploading" style="width: 250px; height: 150px" controls>
                            <source :src="getMediaSrc(file)" />
                            {{ $t('Your browser does not support HTML5 video.') }}
                        </video>
                        <div v-else class="position-relative text-align-center">
                            <div class="position-absolute w-100 h-100">
                                <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                            </div>
                        </div>
                        <div
                            v-if="!file.uploading"
                            class="position-absolute"
                            style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                        >
                            <span @click="handleRemove(file)">
                                <i class="fa fa-trash text-danger"></i>
                            </span>
                        </div>
                    </div>
                </el-upload>
            </template>

            <template v-if="question.isAudio">
                <el-upload
                    ref="upload"
                    class="file-upload-audio"
                    list-type="picture-card"
                    action="/mobile/file/upload"
                    multiple
                    accept="audio/*"
                    :file-list="questionFiles"
                    :auto-upload="false"
                    :on-change="onFileUpload"
                >
                    <i slot="trigger" class="el-icon-plus"></i>
                    <el-button
                        class="el-upload el-upload--picture-card"
                        style="margin-left: 10px"
                        icon="el-icon-upload2"
                        @click="onOpenVisitLibraryDialog('Audio')"
                    >
                        {{ $t('Visit Library') }}
                    </el-button>
                    <div slot="file" slot-scope="{ file }">
                        <audio v-if="!file.uploading" style="width: 250px; height: 150px" controls>
                            <source :src="getMediaSrc(file)" />
                            {{ $t('Your browser does not support HTML5 audio.') }}
                        </audio>
                        <div v-else class="position-relative text-align-center">
                            <div class="position-absolute w-100 h-100">
                                <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                            </div>
                        </div>
                        <div
                            v-if="!file.uploading"
                            class="position-absolute"
                            style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                        >
                            <span @click="handleRemove(file)">
                                <i class="fa fa-trash text-danger"></i>
                            </span>
                        </div>
                    </div>
                </el-upload>
            </template>

            <template v-if="question.isSignature">
                <div
                    v-if="question.data.return_value"
                    style="display: flex; align-items: center; background: #fff"
                    @click="openSignaturePad"
                >
                    <img
                        width="150"
                        :src="'data:Image/jpeg;base64,' + question.data.return_value"
                    />
                </div>

                <el-button v-else type="primary" size="small" @click="openSignaturePad">
                    <i class="fa fa-sign-language"></i>
                </el-button>

                <el-dialog :visible.sync="signatureDialogVisible">
                    <vuesignature
                        ref="signature"
                        w="100%"
                        h="200px"
                        style="border: 1px solid gray; margin-top: 10px"
                    ></vuesignature>
                    <div slot="footer" class="dialog-footer">
                        <el-button @click="saveSign" type="primary" size="small">{{
                            $t('Save')
                        }}</el-button>
                        <el-button type="text" @click="clearSign" size="small">{{
                            $t('Clear')
                        }}</el-button>
                    </div>
                </el-dialog>
            </template>

            <htmleditor
                v-if="question.isHtmlEditor"
                :question="question"
                v-model="question.data.return_value"
                class="w-100"
            ></htmleditor>

            <building v-if="question.isBuilding" :question="question" :visit="visit"></building>

            <visitlocationaddress
                v-if="question.isVisitLocationAddress"
                :question="question"
                :visit="visit"
            ></visitlocationaddress>

            <visitlocationtradecode
                v-if="question.isVisitLocationTradeCode"
                :question="question"
                :visit="visit"
            ></visitlocationtradecode>

            <rsalossestimates
                v-if="question.isRSALossEstimate"
                :question="question"
                :visit="visit"
            ></rsalossestimates>

            <qra v-if="question.isQRA" @onAddRI="onRITap" :question="question" :visit="visit"></qra>

            <ncbpreviewcheck
                v-if="question.isNCBPreviewCheck"
                :form="form"
                :sections="sections"
                :questions="questions"
                :question="question"
                ref="ncbPreviewCheck"
            ></ncbpreviewcheck>

            <ncbplacarding
                v-if="question.isNCBPlacard"
                :form="form"
                :sections="sections"
                :question="question"
            ></ncbplacarding>
            <template v-if="question.isDownloadComponent">
                <el-button
                    id="download_button"
                    size="large"
                    type="primary"
                    @click="onDownloadFileClick(question)"
                    >Download</el-button
                >
            </template>
        </el-form-item>
        <el-dialog
            :visible.sync="documentDialogVisible"
            :title="question.caption"
            class="document-upload-dialog"
        >
            <span class="form-label">{{ $t('Image') }}</span>
            <el-upload
                ref="uploadImage"
                class="file-upload-image"
                list-type="picture-card"
                action="/mobile/file/upload"
                multiple
                accept="image/*"
                :file-list="imageFiles"
                :auto-upload="false"
                :on-change="onImageUpload2"
            >
                <i slot="default" class="el-icon-plus"></i>

                <div slot="file" slot-scope="{ file }">
                    <img
                        v-if="!file.uploading"
                        class="el-upload-list__item-thumbnail"
                        :src="getImageSrc(file)"
                        alt
                    />
                    <div v-else class="position-relative text-align-center">
                        <div class="position-absolute w-100 h-100">
                            <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                        </div>
                    </div>
                    <div
                        v-if="!file.uploading"
                        class="position-absolute"
                        style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                    >
                        <span @click="handleImageTag(file.file_storage_id)">
                            <i class="fa fa-tag"></i>
                        </span>
                        <span @click="handleDocumentRemove(file, 'uploadImage')">
                            <i class="fa fa-trash text-danger"></i>
                        </span>
                        <span @click="handlePictureCardPreview(file)">
                            <i class="el-icon-zoom-in"></i>
                        </span>
                    </div>
                </div>
            </el-upload>
            <span class="form-label">{{ $t('Video') }}</span>
            <el-upload
                ref="uploadVideo"
                class="file-upload-video"
                list-type="picture-card"
                action="/mobile/file/upload"
                multiple
                accept="video/*"
                :file-list="videoFiles"
                :auto-upload="false"
                :on-change="onDocumentUpload"
            >
                <i slot="default" class="el-icon-plus"></i>
                <div slot="file" slot-scope="{ file }">
                    <video v-if="!file.uploading" style="width: 250px; height: 96px" controls>
                        <source :src="getMediaSrc(file)" />
                        {{ $t('Your browser does not support HTML5 video.') }}
                    </video>
                    <div v-else class="position-relative text-align-center">
                        <div class="position-absolute w-100 h-100">
                            <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                        </div>
                    </div>
                    <div
                        v-if="!file.uploading"
                        class="position-absolute"
                        style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                    >
                        <span @click="handleDocumentRemove(file, 'uploadVideo')">
                            <i class="fa fa-trash text-danger"></i>
                        </span>
                    </div>
                </div>
            </el-upload>
            <span class="form-label">{{ $t('Audio') }}</span>
            <el-upload
                ref="uploadAudio"
                class="file-upload-audio"
                list-type="picture-card"
                action="/mobile/file/upload"
                multiple
                accept="audio/*"
                :file-list="audioFiles"
                :auto-upload="false"
                :on-change="onDocumentUpload"
            >
                <i slot="default" class="el-icon-plus"></i>
                <div slot="file" slot-scope="{ file }">
                    <audio v-if="!file.uploading" style="width: 250px; height: 96px" controls>
                        <source :src="getMediaSrc(file)" />
                        {{ $t('Your browser does not support HTML5 audio.') }}
                    </audio>
                    <div v-else class="position-relative text-align-center">
                        <div class="position-absolute w-100 h-100">
                            <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                        </div>
                    </div>
                    <div
                        v-if="!file.uploading"
                        class="position-absolute"
                        style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                    >
                        <span @click="handleDocumentRemove(file, 'uploadAudio')">
                            <i class="fa fa-trash text-danger"></i>
                        </span>
                    </div>
                </div>
            </el-upload>
            <span class="form-label">{{ $t('Document') }}</span>
            <el-upload
                ref="uploadDocument"
                class="file-upload-docs"
                list-type="picture-card"
                action="/mobile/file/upload"
                multiple
                :accept="documentMimeTypes"
                :file-list="otherFiles"
                :auto-upload="false"
                :on-change="onDocumentUpload"
            >
                <i slot="default" class="el-icon-plus"></i>
                <div slot="file" slot-scope="{ file }">
                    <div v-if="file.uploading" class="position-relative text-align-center">
                        <div class="position-absolute w-100 h-100">
                            <i class="fa fa-spinner fa-spin" style="margin-top: 40%"></i>
                        </div>
                    </div>
                    <template v-else>
                        <a
                            class="display-block text-align-center w-100 h-100"
                            :href="getMediaSrc(file)"
                            :title="file.name"
                            target="_blank"
                            style="line-height: 96px"
                        >
                            <i class="fa fa-file"></i>
                        </a>
                        <div
                            class="position-absolute"
                            style="top: 0; right: 10px; font-size: 20px; cursor: pointer"
                        >
                            <span @click="handleDocumentRemove(file, 'uploadDocument')">
                                <i class="fa fa-trash text-danger"></i>
                            </span>
                        </div>
                    </template>
                </div>
            </el-upload>
            <span class="form-label">{{ $t('Visit Library') }}</span>
            <div>
                <el-button size="large" type="primary" @click="onOpenVisitLibraryDialog()">{{
                    $t('Select from Visit Library')
                }}</el-button>
            </div>
            <span class="form-label">{{ $t('Notes') }}</span>
            <el-input type="textarea" :rows="3" v-model="question.data.notes"></el-input>
            <div slot="footer" class="dialog-footer">
                <el-button type="text" @click="onCloseDocumentUploadDialog" size="small">{{
                    $t('Close')
                }}</el-button>
            </div>
        </el-dialog>
        <el-dialog
            :visible.sync="helpDocumentsDialogVisible"
            :title="question.caption"
            class="help-documents-dialog"
        >
            <template v-if="question.help_text">
                <span class="form-label">{{ $t('Help Text') }}</span>
                <htmleditor v-model="question.help_text" :editable="false"></htmleditor>
            </template>

            <template v-if="question.help_docs && question.help_docs.length > 0">
                <span class="form-label">{{ $t('Help Documents') }}</span>
                <div>
                    <el-card
                        class="pull-left m-5px"
                        v-for="file in question.help_docs"
                        :key="file.id"
                        @click="onOpenMedia(file)"
                    >
                        <template v-if="isImage(file)">
                            <a
                                class="help-file-icon"
                                :href="getMediaSrc(file)"
                                :title="file.name"
                                target="_blank"
                            >
                                <img :src="getImageSrc(file)" alt />
                            </a>
                        </template>
                        <template v-else>
                            <a
                                class="help-file-icon"
                                :href="getMediaSrc(file)"
                                :title="file.name"
                                target="_blank"
                            >
                                <i class="fa fa-file"></i>
                            </a>
                            <div class="help-file-name text-overflow-ellipsis">
                                {{ file.file_storage_id }}
                            </div>
                        </template>
                    </el-card>
                    <div class="clearfix"></div>
                </div>
            </template>

            <div slot="footer" class="dialog-footer">
                <el-button type="text" @click="onCloseHelpDocumentsDialog" size="small">{{
                    $t('Close')
                }}</el-button>
            </div>
        </el-dialog>
        <el-dialog :visible.sync="dialogImageVisible">
            <img width="100%" :src="dialogImageUrl" alt />
        </el-dialog>
        <fileselectormodal
            v-if="visitFileLibraryDialogVisible"
            v-model="visitFileLibraryDialogVisible"
            :visit_id="visitId"
            :question="question"
            :fileType="visitFileLibraryType"
        >
        </fileselectormodal>
        <imagetag
            v-if="imageTagModalVisible"
            v-model="imageTagModalVisible"
            :fileStorage="selectedFileStorage"
        ></imagetag>
        <questioncomment
            v-if="commentDialogVisible"
            v-model="commentDialogVisible"
            :question="question"
            :contextCalendar="false"
        ></questioncomment>
        <rimodal
            v-if="riDialogVisible"
            v-model="riDialogVisible"
            :question="question"
            :visit="visit"
        >
        </rimodal>
    </el-form>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import eventBus from '@app/src/lib/helpers/eventbus';
import { DEFAULT_ERROR_MESSAGE } from '@app/common/utils/constants';
import Logger from '@app/common/utils/logger';
import vuesignature from 'vue-signature';
import resizeimage from '@/lib/util/resizeimage';
import Model from '@/lib/data/Model';
import { RuleParser } from '@/lib/util/ruleparser';
import FileStorageItem from '@/data/FileStorageItem';
import FormQuestion from '@/data/FormQuestion';
import Form from '@/data/Form';
import fileselectormodal from '../fileselectormodal.vue';
import imagetag from '../imagetag.vue';
import MoneyInput from './moneyInput.vue';
import selection from './selection.vue';
import addresscoordinatesfield from '../addresscoordinatesfield.vue';
import textfield from './textfield.vue';
import timepicker from '../timepicker.vue';
import ncbpreviewcheck from './ncbpreview/ncbpreviewcheck.vue';
import ncbplacarding from './ncbplacarding.vue';
import Htmleditor from '../htmleditor/htmleditor.vue';
import numberfield from './numberfield.vue';
import questioncomment from '../questioncomment.vue';
import building from './rsabuilding/index.vue';
import rsalossestimates from './rsalossestimates.vue';
import visitlocationaddress from './visitlocationaddress.vue';
import visitlocationtradecode from './visitlocationtradecode.vue';
import rimodal from '../riskimprovement/rimodal.vue';
import qra from '../riskimprovement/qra.vue';

export default {
    name: 'question',
    components: {
        addresscoordinatesfield,
        selection,
        MoneyInput,
        fileselectormodal,
        imagetag,
        timepicker,
        vuesignature,
        textfield,
        ncbpreviewcheck,
        ncbplacarding,
        Htmleditor,
        numberfield,
        questioncomment,
        building,
        rsalossestimates,
        visitlocationaddress,
        visitlocationtradecode,
        rimodal,
        qra,
    },
    props: {
        question: { type: FormQuestion, required: true },
        visitId: null,
        form: { type: Form, required: true },
        sections: { type: Array, required: false },
        section: { type: Object, required: false },
        questions: { type: Array, required: false },
        calcMethod: null,
        visit: null,
    },
    data() {
        return {
            maxDate: new Date(),
            datePickerOptions() {
                return {
                    disabledDate: (date) => date.getTime() > moment().add(1, 'days').toDate(),
                };
            },
            signatureDialogVisible: false,
            signatureImageUrl: null,
            questionFiles: [],
            dialogImageVisible: false,
            dialogImageUrl: null,
            auth_token: Model.getAuthToken(),
            documentDialogVisible: false,
            riDialogVisible: false,
            helpDocumentsDialogVisible: false,
            ruleparser: new RuleParser(),
            visitFileLibraryDialogVisible: false,
            visitFileLibraryType: null,
            imageTagModalVisible: false,
            selectedFileStorage: null,
            documentMimeTypes: [
                { ext: '.doc', mimeType: 'application/msword' },
                { ext: '.dot', mimeType: 'application/msword' },
                {
                    ext: '.docx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                },
                {
                    ext: '.dotx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                },
                { ext: '.docm', mimeType: 'application/vnd.ms-word.document.macroEnabled.12' },
                { ext: '.dotm', mimeType: 'application/vnd.ms-word.template.macroEnabled.12' },
                // eslint-disable-next-line sonarjs/no-duplicate-string
                { ext: '.xls', mimeType: 'application/vnd.ms-excel' },
                { ext: '.xlt', mimeType: 'application/vnd.ms-excel' },
                { ext: '.xla', mimeType: 'application/vnd.ms-excel' },
                {
                    ext: '.xlsx',
                    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                },
                {
                    ext: '.xltx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
                },
                { ext: '.xlsm', mimeType: 'application/vnd.ms-excel.sheet.macroEnabled.12' },
                { ext: '.xltm', mimeType: 'application/vnd.ms-excel.template.macroEnabled.12' },
                { ext: '.xlam', mimeType: 'application/vnd.ms-excel.addin.macroEnabled.12' },
                { ext: '.xlsb', mimeType: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' },
                // eslint-disable-next-line sonarjs/no-duplicate-string
                { ext: '.ppt', mimeType: 'application/vnd.ms-powerpoint' },
                { ext: '.pot', mimeType: 'application/vnd.ms-powerpoint' },
                { ext: '.pps', mimeType: 'application/vnd.ms-powerpoint' },
                { ext: '.ppa', mimeType: 'application/vnd.ms-powerpoint' },
                {
                    ext: '.pptx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                },
                {
                    ext: '.potx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.presentationml.template',
                },
                {
                    ext: '.ppsx',
                    mimeType:
                        'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
                },
                { ext: '.ppam', mimeType: 'application/vnd.ms-powerpoint.addin.macroEnabled.12' },
                {
                    ext: '.pptm',
                    mimeType: 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
                },
                {
                    ext: '.potm',
                    mimeType: 'application/vnd.ms-powerpoint.template.macroEnabled.12',
                },
                {
                    ext: '.ppsm',
                    mimeType: 'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
                },
                { ext: '.mdb', mimeType: 'application/vnd.ms-access' },
                { ext: '.pdf', mimeType: 'application/pdf' },
                { ext: '.rar', mimeType: 'application/vnd.rar' },
                { ext: '.tar', mimeType: 'application/x-tar' },
                { ext: '.zip', mimeType: 'application/zip' },
                { ext: '.odp', mimeType: 'application/vnd.oasis.opendocument.presentation' },
                { ext: '.ods', mimeType: 'application/vnd.oasis.opendocument.spreadsheet' },
                { ext: '.odt', mimeType: 'application/vnd.oasis.opendocument.text' },
                { ext: '.json', mimeType: 'application/json' },
                { ext: '.gz', mimeType: 'application/gzip' },
                { ext: '.csv', mimeType: 'text/csv' },
                { ext: '.txt', mimeType: 'text/plain' },
                { ext: '.xml', mimeType: 'application/xml' },
                { ext: '.xml', mimeType: 'text/xml' },
            ]
                .map((item) => item.mimeType)
                .join(','),
            commentDialogVisible: false,
        };
    },
    created() {
        if (this.question.isMedia) {
            this.questionFiles = this.question.data.return_value
                ? this.question.data.return_value.split(',').map((id) => ({
                      id,
                  }))
                : [];
        }
    },
    mounted() {
        eventBus.$on('saveForm', () => {
            if (this.$refs.form) {
                this.$refs.form.validate((isValid) => {
                    if (!isValid) {
                        // do something
                    }
                });
            }
        });

        eventBus.$on('quesHided', (message) => {
            const question = this.question.id == message.ref_id ? this.question : null;

            if (question) {
                question.data.return_value = '';
                question.visible = false;
                this.form.setHiddenQuestions(question.id);
            }
        });

        eventBus.$on('quesShown', (message) => {
            // gosterildigi yerde default_valuyu set etmek gerek.
            const question = this.question.id == message.ref_id ? this.question : null;

            if (question) {
                question.visible = true;
                /* formun ilk yuklenmesinde default visibility si false olan soru daha sonra 
                yuklenen bir soru ile visible oluyorsa return value yu kaybetmeyip old_return_value den almak icin
                konuldu  */
                if (question.data.old_return_value) {
                    question.data.return_value = question.data.old_return_value;
                } else if (question.default_value && !question.data.return_value) {
                    question.data.return_value = question.default_value;
                }
                this.form.setHiddenQuestions(question.id, true);
            }
        });

        this.ruleparser
            .on('hide', (input) => {
                if (input.result) {
                    const elementId = `${input.action.ref_id}_${input.action.ref_table}`;

                    const elms = document.querySelectorAll(`[id='${elementId}']`);
                    for (let i = 0, l = elms.length; i < l; i += 1) {
                        elms[i].style.setProperty('display', 'none', 'important');
                    }
                    if (input.action.ref_table === 'cdt') {
                        eventBus.$emit('optionHided', { ref_id: input.action.ref_id, visible: 0 });
                    } else if (input.action.ref_table === 'fq') {
                        eventBus.$emit('quesHided', { ref_id: input.action.ref_id, visible: 0 });
                    } else {
                        eventBus.$emit('sectionHided', { ref_id: input.action.ref_id, visible: 0 });
                    }
                }
            })
            .on('show', (input) => {
                if (input.result) {
                    const elementId = `${input.action.ref_id}_${input.action.ref_table}`;
                    const elms = document.querySelectorAll(`[id='${elementId}']`);

                    /* shared mode da section idyi kontrol et */
                    let affectedSection;
                    if (this.isInSharedMode && input.action.ref_table == 'sctn') {
                        affectedSection = this.sections.find(
                            (item) => item.id == input.action.ref_id
                        );
                    }
                    for (let i = 0, l = elms.length; i < l; i += 1) {
                        /* shared mode da degilse eskisi gibi devam et yada */
                        /* shared moda da ise ve etkilenen section isVisibleInSharedMode sa goster  */
                        if (
                            !this.isInSharedMode ||
                            (this.isInSharedMode && affectedSection?.isVisibleInSharedMode)
                        ) {
                            elms[i].style.setProperty('display', 'block', 'important');
                        }
                    }
                    if (input.action.ref_table === 'cdt') {
                        eventBus.$emit('optionShown', { ref_id: input.action.ref_id, visible: 1 });
                    } else if (input.action.ref_table === 'fq') {
                        eventBus.$emit('quesShown', { ref_id: input.action.ref_id, visible: 1 });
                    } else {
                        eventBus.$emit('sectionShown', { ref_id: input.action.ref_id, visible: 1 });
                    }
                }
            });

        const $question = document.getElementById(`${this.question.id}_fq`);

        if ($question) {
            $question.style.setProperty(
                'display',
                this.question.default_visibility ? 'block' : 'none',
                'important'
            );

            // eger defaultu 0 sa, default_value yu sifirla
            // ma cunku, shown olucaksa formdaki soruya gore retur value kayboluyor.
            // ama baska nereyi etkiliyor??
            if (!this.question.default_visibility) {
                this.question.data.old_return_value = this.question.data.return_value;
                this.question.data.return_value = null;
            }
            // defult_visibility si false ise return_value yu temizle
            // lakin show olunca return_value default_value olarak set edilmemli.

            if (!this.question.default_visibility && !this.question.data.return_value) {
                eventBus.$emit('quesHided', { ref_id: this.question.id, visible: 0 });
            }
        }
    },
    computed: {
        showCaption() {
            return !this.question.isQRA;
        },
        showAddRi() {
            return this.question.show_add_ri;
        },
        checkboxListSelections: {
            get() {
                return this.question.data.return_value
                    ? this.question.data.return_value.split(',')
                    : null;
            },
            set(value) {
                value = value || [];
                this.question.data.return_value = value.join(',');
            },
        },
        imageFiles() {
            return this.question.data.docs.filter((item) => this.isImage(item));
        },
        videoFiles() {
            return this.question.data.docs.filter((item) => this.isVideo(item));
        },
        audioFiles() {
            return this.question.data.docs.filter((item) => this.isAudio(item));
        },
        otherFiles() {
            return this.question.data.docs.filter((item) => this.isDocument(item));
        },
        isInSharedMode() {
            return this.$route.meta.name === 'share';
        },
        coordinates: {
            get() {
                const [lat, lng] = this.question.data.return_value?.split(',') || [];
                return { lat, lng };
            },
            set(value) {
                const { lat, lng } = value;
                this.question.data.return_value = `${lat},${lng}`;
            },
        },
    },
    watch: {
        questionFiles(value) {
            value = value || [];
            this.question.data.return_value = value.map((file) => file.id).join(',');
        },
        'question.data.return_value': {
            handler(v, oldVal) {
                if (this.question.isYesNo) {
                    const cdPre = this.question.control_data.find((cd) => cd.cd_value == oldVal);

                    // cdt id ye ait rulelari al
                    if (cdPre) {
                        const rulesPre = cdPre.rules;

                        // ve islemi geri al
                        this.ruleparser.ruleParserNew(rulesPre, { id: cdPre.id }, true);
                    }
                    // run rule
                    const cd = this.question.control_data.find((data) => data.cd_value == v);
                    if (cd) {
                        const { rules } = cd;
                        this.ruleparser.ruleParserNew(rules, {
                            id: cd.id,
                        });
                    }
                }
                // visit library den yuklenmisse
                if (this.question.isMedia) {
                    this.questionFiles = this.question.data.return_value
                        ? this.question.data.return_value.split(',').map((id) => ({
                              id,
                          }))
                        : [];
                }

                eventBus.$emit('question_value_changed', this.question, v, oldVal);
            },
        },
    },
    methods: {
        onDownloadFileClick(question) {
            window.open(this.getMediaSrcById(question.default_value));
        },
        handleImageTag(fileId) {
            const fileStorageItem = new FileStorageItem({ id: fileId });

            fileStorageItem.getById({
                params: {
                    id: fileStorageItem.id,
                },
                beforesend: () => {
                    this.loading = true;
                },
                complete: () => {
                    this.loading = false;
                },
                success: (op, response) => {
                    this.selectedFileStorage = new FileStorageItem(response.data);
                    this.imageTagModalVisible = true;
                },
                failure: (op, response) => {
                    this.$notify.error({
                        title: this.$t('Error'),
                        message:
                            response && response.meta
                                ? response.meta.display_message
                                : this.$t(DEFAULT_ERROR_MESSAGE),
                    });
                },
            });
        },
        onOpenVisitLibraryDialog(type) {
            this.visitFileLibraryType = type;
            this.visitFileLibraryDialogVisible = true;
        },
        onImageUpload(file, files) {
            const self = this;
            const fileName = file.name;

            file.uploading = true;

            resizeimage
                .resizeImage({
                    file,
                    maxSize: 1280,
                })
                .then((resizedImage) => {
                    resizedImage.lastModifiedDate = new Date();
                    resizedImage.name = fileName;
                    Logger.log('upload resized image');
                    self.onFileUpload(resizedImage, files);
                })
                .catch((err) => {
                    Logger.error(err);
                });
        },
        onImageUpload2(file, files) {
            const self = this;
            const fileName = file.name;

            file.uploading = true;

            resizeimage
                .resizeImage({
                    file,
                    maxSize: 1280,
                })
                .then((resizedImage) => {
                    resizedImage.lastModifiedDate = new Date();
                    resizedImage.name = fileName;
                    Logger.log('upload resized image');
                    self.onDocumentUpload(resizedImage, files);
                })
                .catch((err) => {
                    Logger.error(err);
                });
        },
        onFileUpload(file, files) {
            const view = this;
            // @todo: gitmeden limit check edilecek
            file.uploading = true;

            function onError(err) {
                file.uploading = false;
                files.splice(files.indexOf(file), 1);
                view.$notify.error({
                    title: view.$t('Error'),
                    message: err ? err.message : view.$t(DEFAULT_ERROR_MESSAGE),
                });
            }

            axios
                .get('/mobile/file/upload/ticket', {
                    headers: {
                        Authorization: `Bearer ${this.auth_token}`,
                        'File-Name': file.name,
                        'Visit-Id': this.visitId,
                        'Ref-Type': 'form_ques_data',
                        'Convert-Video': 'true',
                    },
                })
                .then((response) => {
                    if (response.data.meta.return_code == 0) {
                        const ticket = response.data.data.id;
                        const dbEnv = response.data.data.db_env;

                        axios
                            .post('https://fs.virtualriskspace.com/upload', file.raw, {
                                headers: {
                                    'Content-Type': 'application/octet-stream',
                                    'DB-Env': dbEnv,
                                    Ticket: ticket,
                                },
                            })
                            .then((_response) => {
                                file.uploading = false;
                                file.id = _response.data;
                                file.mime_type = file.raw.type;
                                this.questionFiles.push(file);

                                axios
                                    .post(
                                        '/mobile/file/upload/finalize',
                                        {
                                            storage_id: file.id,
                                            visit_id: this.visitId,
                                        },
                                        {
                                            headers: {
                                                Authorization: `Bearer ${this.auth_token}`,
                                            },
                                        }
                                    )
                                    .then((__response) => {
                                        if (__response.data.meta.return_code != 0) {
                                            this.$notify.error({
                                                title: this.$t('Error'),
                                                message:
                                                    __response &&
                                                    __response.data &&
                                                    __response.data.meta
                                                        ? __response.data.meta.display_message
                                                        : this.$t(DEFAULT_ERROR_MESSAGE),
                                            });
                                        }
                                    })
                                    .catch((err) => {
                                        this.$notify.error({
                                            title: this.$t('Error'),
                                            message: err
                                                ? err.message
                                                : this.$t(DEFAULT_ERROR_MESSAGE),
                                        });
                                    });
                            })
                            .catch(onError);
                    } else {
                        onError(
                            new Error(
                                response && response.data && response.data.meta
                                    ? response.data.meta.display_message
                                    : this.$t(DEFAULT_ERROR_MESSAGE)
                            )
                        );
                    }
                })
                .catch(onError);
        },
        handleRemove(file) {
            this.$confirm(
                this.$t('The file will be deleted when you save the form. Continue?'),
                this.$t('Warning'),
                {
                    confirmButtonText: this.$t('OK'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning',
                }
            )
                .then(() => {
                    this.removeFile(file);
                })
                .catch(() => {
                    // do something
                });
        },
        removeFile(file) {
            this.$refs.upload.uploadFiles.splice(this.$refs.upload.uploadFiles.indexOf(file), 1);
            this.questionFiles.splice(this.questionFiles.indexOf(file), 1);
        },
        getImageSrc(file, width = 150, height = 150) {
            let resolutionsQuery = '';

            if (width > 0) {
                resolutionsQuery += `&w=${width}`;
            }

            if (height > 0) {
                resolutionsQuery += `&h=${height}`;
            }

            return `${window.location.origin}/file?id=${
                file.file_storage_id || file.id
            }${resolutionsQuery}&auth_token=${this.auth_token}`;
        },
        getMediaSrc(file) {
            return `${window.location.origin}/file?id=${
                file.file_storage_id || file.id
            }&auth_token=${this.auth_token}`;
        },
        getMediaSrcById(fileId) {
            return `${window.location.origin}/file?id=${fileId}&auth_token=${this.auth_token}`;
        },
        onOpenMedia(file) {
            window.open(this.getMediaSrc(file));
        },
        saveSign() {
            const jpg = this.$refs.signature.save('image/jpeg');
            if (jpg) {
                [, this.question.data.return_value] = this.$refs.signature
                    .save('image/jpeg')
                    .split(',');
                this.signatureDialogVisible = false;
            }
        },
        clearSign() {
            this.$refs.signature.clear();
        },
        openSignaturePad() {
            this.signatureDialogVisible = true;

            this.$nextTick(() => {
                // Wait for canvas to be rendered, due to a resize bug in vue-signature
                setTimeout(() => {
                    try {
                        this.clearSign();
                        if (this.question.data.return_value) {
                            this.$refs.signature.fromDataURL(
                                `data:image/jpeg;base64,${this.question.data.return_value}`
                            );
                        }
                    } catch (error) {
                        Logger.error(error);
                    }
                }, 300);
            });
        },
        handlePictureCardPreview(file) {
            this.dialogImageUrl = this.getMediaSrc(file);
            this.dialogImageVisible = true;
        },
        onDocumentUploadTap() {
            this.documentDialogVisible = true;
        },
        onRITap() {
            this.riDialogVisible = true;
        },
        onCommentButtonTap() {
            this.commentDialogVisible = true;
        },
        onCloseDocumentUploadDialog() {
            this.documentDialogVisible = false;
        },
        onDocumentUpload(file, files) {
            const view = this;
            // @todo: gitmeden limit check edilecek
            file.uploading = true;

            function onError(err) {
                file.uploading = false;
                files.splice(files.indexOf(file), 1);
                view.$notify.error({
                    title: view.$t('Error'),
                    message: err ? err.message : view.$t(DEFAULT_ERROR_MESSAGE),
                });
            }

            axios
                .get('/mobile/file/upload/ticket', {
                    headers: {
                        Authorization: `Bearer ${this.auth_token}`,
                        'File-Name': encodeURI(file.name),
                        'Visit-Id': this.visitId,
                        'Ref-Type': 'form_ques_data_docs',
                        'Convert-Video': 'true',
                    },
                })
                .then((response) => {
                    if (response.data.meta.return_code == 0) {
                        const ticket = response.data.data.id;
                        const dbEnv = response.data.data.db_env;

                        axios
                            .post('https://fs.virtualriskspace.com/upload', file.raw, {
                                headers: {
                                    'Content-Type': 'application/octet-stream',
                                    'DB-Env': dbEnv,
                                    Ticket: ticket,
                                },
                            })
                            .then((_response) => {
                                file.uploading = false;
                                file.file_storage_id = _response.data;
                                file.mime_type = file.raw.type;
                                this.question.data.docs.add(file);

                                axios
                                    .post(
                                        '/mobile/file/upload/finalize',
                                        {
                                            storage_id: file.file_storage_id,
                                            visit_id: this.visitId,
                                        },
                                        {
                                            headers: {
                                                Authorization: `Bearer ${this.auth_token}`,
                                            },
                                        }
                                    )
                                    .then((__response) => {
                                        if (__response.data.meta.return_code != 0) {
                                            this.$notify.error({
                                                title: this.$t('Error'),
                                                message:
                                                    __response &&
                                                    __response.data &&
                                                    __response.data.meta
                                                        ? __response.data.meta.display_message
                                                        : this.$t(DEFAULT_ERROR_MESSAGE),
                                            });
                                        }
                                    })
                                    .catch((err) => {
                                        this.$notify.error({
                                            title: this.$t('Error'),
                                            message: err
                                                ? err.message
                                                : this.$t(DEFAULT_ERROR_MESSAGE),
                                        });
                                    });
                            })
                            .catch(onError);
                    } else {
                        onError(
                            new Error(
                                response && response.data && response.data.meta
                                    ? response.data.meta.display_message
                                    : this.$t(DEFAULT_ERROR_MESSAGE)
                            )
                        );
                    }
                })
                .catch(onError);
        },
        handleDocumentRemove(file, ref) {
            this.$confirm(
                this.$t('The file will be deleted when you save the form. Continue?'),
                this.$t('Warning'),
                {
                    confirmButtonText: this.$t('OK'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning',
                }
            )
                .then(() => {
                    this.removeDocumentFile(file, ref);
                })
                .catch(() => {});
        },
        removeDocumentFile(file, ref) {
            this.$refs[ref].uploadFiles.splice(this.$refs[ref].uploadFiles.indexOf(file), 1);
            this.question.data.docs.remove(file);
            file.remove();
        },
        isImage(item) {
            return item.mime_type.indexOf('image/') > -1;
        },
        isVideo(item) {
            return item.mime_type.indexOf('video/') > -1;
        },
        isAudio(item) {
            return item.mime_type.indexOf('audio/') > -1;
        },
        isDocument(item) {
            return !this.isImage(item) && !this.isVideo(item) && !this.isAudio(item);
        },
        onHelpDocumentsClick() {
            this.helpDocumentsDialogVisible = true;
        },
        onCloseHelpDocumentsDialog() {
            this.helpDocumentsDialogVisible = false;
        },
    },
};
</script>

<style lang="scss">
.custom-number-field {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
}
.file-upload-video,
.file-upload-audio {
    .el-upload-list__item {
        width: 250px !important;
    }
}
.document-upload-button {
    position: absolute;
    top: -25px;
    right: 3px;
    cursor: pointer;
}

.document-ri-button {
    position: absolute;
    top: -25px;
    right: 43px;
    cursor: pointer;
}
.document-comment-button {
    position: absolute;
    top: -25px;
    right: 25px;
    cursor: pointer;
}
.document-upload-dialog {
    .el-upload--picture-card {
        width: 98px;
        height: 98px;
        line-height: 96px;
    }
    .el-upload-list--picture-card .el-upload-list__item {
        width: 98px;
        height: 98px;
    }
}
.help-documents-button {
    position: absolute;
    top: -25px;
    right: 23px;
    cursor: pointer;
}
.help-documents-dialog {
    .el-card {
        width: 120px;
        height: 120px;

        .el-card__body {
            padding: 0;
            width: 100%;
            height: 100%;
            position: relative;

            .help-file-icon {
                display: block;
                width: 100%;
                height: 100%;
                line-height: 120px;
                text-align: center;
            }

            .help-file-name {
                display: block;
                position: absolute;
                width: 100%;
                bottom: 0;
                text-align: center;
                line-height: 32px;
                padding: 0 4px;
                cursor: default;
            }
        }
    }
}

.label-image-placeholder {
    width: 300px;
    height: 150px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}
</style>
